// Pure view-model helpers for the redesigned alarms page (ACTIVE / ALL ALARMS /
// HISTORY). Everything here is a DECISION, not a render: which species a
// definition is, what the URL says the page is showing, what the footer tiles
// are allowed to claim, and where an SVG polyline's points go. It all lives in
// this module for the same reason `alarmDisplay` does — `cloud/web`'s test
// runner has no DOM, so a decision that cannot be reached without mounting a
// component is a decision that never gets a test.
//
// The honesty rules the redesign inherited are enforced HERE rather than left
// to three screen components to remember:
//
//   - A species is read from the COMPILED trigger class, falling back to the
//     custom block. A class this build has never heard of is 'none' — no
//     sparkline, no threshold tick, no invented phrase (data-mapping trap 2).
//   - The all-clear claim has three outcomes, not two: paused (nothing is being
//     watched, so nothing can need attention), unknown (the boat has never
//     reported a runtime row — an empty band is not a quiet boat), and the
//     earned all-clear. `activeCount === null` is UNKNOWN, never zero.
//   - The mini-bar returns `null` when there is no observed window to scale
//     against. The signal catalog carries no min/max, so a bar drawn on a
//     guessed 0..100 domain would be a fabricated reading (trap 6 / the NOW
//     cell note). No window, no bar.
//   - Sparkline geometry pads its domain to INCLUDE the threshold, so a
//     threshold outside the observed range still draws inside the viewBox
//     instead of being clipped away and reading as "not close".

import { formatRelativeShort } from './deviceSeen';
import { readCustomSpec, readWireTrigger, type AlarmSystemGroup } from './alarmDisplay';

// ---------------------------------------------------------------------------
// Species — what KIND of evidence a row can honestly show.
// ---------------------------------------------------------------------------

/**
 * What a definition's evidence cell may draw.
 *
 * - `analog` — a numeric setpoint exists, so a sparkline, a threshold line and
 *   a mini-bar tick all mean something (`High`, `Low`, `AnchorWatch`).
 * - `binary` — an event, no trend to plot; the honest evidence is past trips
 *   (`On`, `EntryOn`, `Off`).
 * - `stale-watch` — fires on ABSENCE (`DataUnavailable`); a trend of the values
 *   that stopped arriving would say the opposite of what the alarm watches.
 * - `none` — no compiled rule (pending / disabled / older cloud), or a class
 *   this build does not know. Render the row setpoint-free.
 */
export type AlarmSpecies = 'analog' | 'binary' | 'stale-watch' | 'none';

const ANALOG_CLASSES = new Set(['High', 'Low', 'AnchorWatch']);
// `CycleCount` (bilge pump cycling, TOM-101) is an event too: it trips when the
// pump has started too often, and its input is the pump's 1/0 run signal. Read
// as analog or "none", its row printed "Now 0" — the pump being off, which says
// nothing about how many times it started. Its honest evidence is its trips.
const BINARY_CLASSES = new Set(['On', 'EntryOn', 'Off', 'CycleCount']);

/**
 * Classify one definition. There is no `species` field on the wire — the
 * compiled trigger class is the truth, and a custom definition with no compiled
 * rule yet falls back to its authored class so the builder's own row does not
 * go blank between save and compile.
 */
export function alarmSpecies(def: {
  readonly trigger?: unknown;
  readonly custom?: unknown;
}): AlarmSpecies {
  const cls = readWireTrigger(def)?.class ?? readCustomSpec(def)?.triggerClass;
  if (cls === undefined) return 'none';
  if (ANALOG_CLASSES.has(cls)) return 'analog';
  if (BINARY_CLASSES.has(cls)) return 'binary';
  if (cls === 'DataUnavailable') return 'stale-watch';
  return 'none';
}

// ---------------------------------------------------------------------------
// View + filter, carried in the URL.
// ---------------------------------------------------------------------------

/** Which of the three screens is mounted. */
export type AlarmsView = 'active' | 'all' | 'history';

/** The ALL ALARMS enabled-state filter. */
export type AlarmsFilter = 'all' | 'on' | 'off';

/** The overview a bare `/alarms` shows: incidents and configured alarms. */
export const DEFAULT_ALARMS_VIEW: AlarmsView = 'all';

/** The filter a bare ALL ALARMS shows. */
export const DEFAULT_ALARMS_FILTER: AlarmsFilter = 'all';

const VIEWS: readonly AlarmsView[] = ['active', 'all', 'history'];
const FILTERS: readonly AlarmsFilter[] = ['all', 'on', 'off'];

/**
 * Read `?view=` — anything unrecognised (a hand-typed URL, a stale bookmark
 * from a future build) falls back to the default rather than mounting nothing.
 */
export function parseAlarmsView(raw: string | null | undefined): AlarmsView {
  const value = (raw ?? '').trim().toLowerCase();
  return VIEWS.includes(value as AlarmsView) ? (value as AlarmsView) : DEFAULT_ALARMS_VIEW;
}

/** Read `?filter=`, same degrade rule as {@link parseAlarmsView}. */
export function parseAlarmsFilter(raw: string | null | undefined): AlarmsFilter {
  const value = (raw ?? '').trim().toLowerCase();
  return FILTERS.includes(value as AlarmsFilter) ? (value as AlarmsFilter) : DEFAULT_ALARMS_FILTER;
}

/**
 * Read `?editing=` — the id of the ONE row whose inline editor is open. Blank
 * or missing means no editor. Nothing is validated against the alarm list here:
 * an id that matches no row simply opens nothing, which is what a stale link
 * should do.
 */
export function parseEditingId(raw: string | null | undefined): string | null {
  const value = (raw ?? '').trim();
  return value.length > 0 ? value : null;
}

/**
 * The search-param values for a view+filter+open-editor, with DEFAULTS OMITTED
 * (`null` means "delete this param"). A plain visit to the page should leave a
 * clean `/alarms` in the address bar; only a deliberate move away from the
 * default earns a query string worth sharing.
 *
 * Every key is always PRESENT in the result, carrying `null` when it is to be
 * dropped. The caller deletes on null, so a key that went missing here would
 * leave the old value in the URL instead of clearing it — which for `editing`
 * would silently reopen an editor on a screen the owner has left.
 */
export function serializeAlarmsParams(state: {
  readonly view: AlarmsView;
  readonly filter: AlarmsFilter;
  readonly editing?: string | null;
}): { view: string | null; filter: string | null; editing: string | null } {
  const onAll = state.view === 'all';
  return {
    view: state.view === DEFAULT_ALARMS_VIEW ? null : state.view,
    // The filter belongs to ALL ALARMS only; carrying `filter=off` around on
    // the ACTIVE screen would deep-link the footer tiles to a stale answer.
    filter: onAll && state.filter !== DEFAULT_ALARMS_FILTER ? state.filter : null,
    // Same rule, and for a stronger reason: the inline editor only exists on
    // ALL ALARMS, so an `editing` id riding along on ACTIVE or HISTORY would
    // reopen a half-typed panel the moment the owner came back.
    editing: onAll ? parseEditingId(state.editing) : null,
  };
}

// ---------------------------------------------------------------------------
// Counts — the segmented selector's badges and the ALL toolbar's chips.
// ---------------------------------------------------------------------------

/** The four numbers the chrome shows. */
export interface AlarmCounts {
  /**
   * The SURFACED set's size — the same definition `summarizeAlerts` uses for
   * the nav badge (Active / Silenced / Acknowledged / AwaitingAcknowledge).
   * The design's narrower "firing" wording would make the segment and the badge
   * disagree, which is worse than either wording alone.
   */
  readonly active: number;
  /** Every non-retired definition, enabled or not, pending or not. */
  readonly total: number;
  readonly on: number;
  readonly off: number;
}

/** Count the chrome's badges from the two view lists the page already derives. */
export function alarmCounts(
  activeViews: readonly { readonly id: string }[],
  configuredViews: readonly { readonly enabled: boolean }[]
): AlarmCounts {
  let on = 0;
  for (const view of configuredViews) if (view.enabled) on += 1;
  return {
    active: activeViews.length,
    total: configuredViews.length,
    on,
    off: configuredViews.length - on,
  };
}

// ---------------------------------------------------------------------------
// The ACTIVE screen's footer tiles.
// ---------------------------------------------------------------------------

/** What tile ① claims: how many alarms are actually being evaluated. */
export interface WatchingTile {
  /**
   * Enabled, compiled, not runtime-disabled, and not already shown in the band
   * above. "Watching" is a claim about what the boat is EVALUATING — a
   * compile-pending row is configured, not watching, and counting it would
   * promise cover that does not exist.
   */
  readonly count: number;
  /**
   * "40 s ago" for the newest reading anywhere on the boat, or `null` when
   * nothing has ever been read (the tile then says nothing about freshness
   * rather than printing a zero).
   */
  readonly readingAgoText: string | null;
}

/**
 * The newest reading anywhere on the boat, in epoch ms, or `null` when this
 * boat has never reported one.
 *
 * There are TWO sources and a quiet boat only has the second (data-mapping trap
 * 6). `lastSampleAtMs` is the newest sample the LIVE socket has seen in this
 * session — it is `null` on a page opened against a boat that has not spoken
 * since, which is most of the fleet most of the time. The signal catalog is the
 * cloud's own store and remembers when each path last carried a value, so a
 * boat whose last sample landed eleven days ago still has an honest answer.
 * Reading only the live half is what made a boat with eleven days of stored
 * readings claim "No readings yet".
 *
 * The newest of the two wins rather than "live, else catalog": both are real
 * observations of the same fact, and the catalog can be the fresher one when
 * the socket opened after the last stored sample.
 *
 * An unparseable or absent `lastValueAt` is skipped, not treated as zero — the
 * catalog nulls those fields for a path it has metadata but no value for.
 */
export function latestReadingMs(input: {
  /** `useBoatChrome().lastSampleAtMs` — newest live sample, any path. */
  readonly lastSampleAtMs: number | null | undefined;
  /** `useSignalCatalog(boatId).signals` — the stored half. */
  readonly catalogSignals: readonly { readonly lastValueAt: string | null }[];
}): number | null {
  let newest: number | null =
    typeof input.lastSampleAtMs === 'number' && Number.isFinite(input.lastSampleAtMs)
      ? input.lastSampleAtMs
      : null;
  for (const signal of input.catalogSignals) {
    if (signal.lastValueAt === null) continue;
    const ms = Date.parse(signal.lastValueAt);
    if (!Number.isFinite(ms)) continue;
    if (newest === null || ms > newest) newest = ms;
  }
  return newest;
}

/** One configured row, as much of it as the tiles need. */
export interface TileRowInput {
  readonly id: string;
  readonly title: string;
  readonly enabled: boolean;
  readonly compilePending: boolean;
  readonly runtimeDisabledReason?: string;
}

/** Build tile ①'s model. */
export function watchingTile(
  views: readonly TileRowInput[],
  surfacedIds: ReadonlySet<string>,
  latestReadingMs: number | null,
  now: number
): WatchingTile {
  let count = 0;
  for (const view of views) {
    if (!view.enabled) continue;
    if (view.compilePending) continue;
    if (view.runtimeDisabledReason !== undefined) continue;
    if (surfacedIds.has(view.id)) continue;
    count += 1;
  }
  return {
    count,
    readingAgoText:
      latestReadingMs === null || !Number.isFinite(latestReadingMs)
        ? null
        : formatRelativeShort(new Date(latestReadingMs).toISOString(), now),
  };
}

/** What tile ② claims: safety alarms the owner switched off. */
export interface SafetyOffTile {
  readonly count: number;
  /** Their titles, in the order the configured list already sorted them. */
  readonly names: string[];
}

/**
 * Safety-group members that are DISABLED. Deliberately narrower than "not
 * watching": a compile-pending safety alarm also leaves the boat unwatched, but
 * it is a different sentence (the row already wears its own "not evaluating"
 * chip) and folding the two would tell an owner to go re-enable something that
 * is already on.
 */
export function safetyOffTile(
  views: readonly TileRowInput[],
  groups: ReadonlyMap<string, AlarmSystemGroup>
): SafetyOffTile {
  const names: string[] = [];
  for (const view of views) {
    if (view.enabled) continue;
    if (groups.get(view.id) !== 'Safety') continue;
    names.push(view.title);
  }
  return { count: names.length, names };
}

// ---------------------------------------------------------------------------
// The all-clear card.
// ---------------------------------------------------------------------------

/**
 * Which of the four empty-band stories to tell.
 *
 * `paused`, `unknown` and `idle` all forbid the All clear headline: paused
 * because nothing is being watched on purpose, unknown because nobody has
 * heard from the boat, and idle because the boat HAS reported and is watching
 * nothing — every enabled alarm is compile-pending or runtime-disabled, or
 * everything is switched off. A green All clear over "0 alarms watching" would
 * be reassurance with no evidence behind it. Only `clear` is earned.
 */
export type AllClearKind = 'paused' | 'unknown' | 'idle' | 'clear';

/** The all-clear card's content, already decided. */
export interface AllClearModel {
  readonly kind: AllClearKind;
  /** The big line. `null` for the two unearned variants — they get prose. */
  readonly headline: string | null;
  /** The sentence under it. Always present. */
  readonly detail: string;
}

/** The unearned-quiet wording for a boat that has never reported a runtime row. */
export const ALARMS_NEVER_REPORTED =
  'Your boat hasn’t reported on these alarms yet, so we can’t say whether anything needs a look.';

/** The unearned-quiet wording for a boat that is watching nothing at all. */
export const ALARMS_NOTHING_WATCHED =
  'Nothing is firing, but no alarms are being watched right now — see All alarms for why.';

/**
 * Decide the all-clear card.
 *
 * @param alarmsPaused the boat's own paused mirror (ADR 0174)
 * @param activeCount the page's null-vs-zero count — `null` means no row
 *   carries a runtime snapshot, which is UNKNOWN and not zero
 * @param watching how many alarms the boat is actually evaluating (tile ①)
 * @param readingAgoText the newest reading's age, or `null`
 */
export function allClearModel(input: {
  readonly alarmsPaused: boolean;
  readonly activeCount: number | null;
  readonly watching: number;
  readonly readingAgoText: string | null;
  /** The paused band's own wording (`ALARMS_PAUSED_BAND_EMPTY`). */
  readonly pausedText: string;
}): AllClearModel {
  if (input.alarmsPaused) {
    return { kind: 'paused', headline: null, detail: input.pausedText };
  }
  if (input.activeCount === null) {
    return { kind: 'unknown', headline: null, detail: ALARMS_NEVER_REPORTED };
  }
  // The boat has reported, nothing is firing, and nothing is being evaluated
  // either — every enabled alarm is pending or runtime-disabled, or everything
  // is off. "All clear · 0 alarms watching" would claim cover that does not
  // exist. (With alarms firing, `watching` 0 just means the whole watch is in
  // the band above — the card is not rendered, and `clear` keeps its meaning.)
  if (input.activeCount === 0 && input.watching === 0) {
    return { kind: 'idle', headline: null, detail: ALARMS_NOTHING_WATCHED };
  }
  const alarms = `${input.watching} ${input.watching === 1 ? 'alarm' : 'alarms'} watching`;
  return {
    kind: 'clear',
    headline: 'All clear',
    detail:
      input.readingAgoText === null ? alarms : `${alarms} · latest reading ${input.readingAgoText}`,
  };
}

// ---------------------------------------------------------------------------
// Sparkline geometry.
// ---------------------------------------------------------------------------

/** One plotted sample — the caller reduces a `RangePoint` to this. */
export interface SeriesPoint {
  /** Epoch ms. */
  readonly t: number;
  readonly v: number;
}

/** The box an SVG is drawn into (its `viewBox`, in user units). */
export interface SparkBox {
  readonly width: number;
  readonly height: number;
  /** Vertical breathing room so a line riding the domain edge is not clipped. */
  readonly padY?: number;
}

/** Everything the evidence SVG needs, already in user units. */
export interface SparkGeometry {
  /** `x,y x,y …` for a `<polyline points>`. Never empty. */
  readonly points: string;
  /** Where the dashed threshold line sits, or `null` when there is none. */
  readonly thresholdY: number | null;
  /** The domain actually used — exported so a caption can state it honestly. */
  readonly min: number;
  readonly max: number;
}

/**
 * Map a series (and optionally a store-domain threshold) into SVG user units.
 *
 * Returns `null` for an empty series: there is no honest line to draw, and a
 * flat baseline at the middle of the box would read as "steady", which is a
 * claim about data we do not have.
 *
 * A FLAT series (every value identical) is real data and does draw — centred,
 * because a zero-height domain has no top or bottom to place it against.
 *
 * The threshold is folded into the domain before scaling, so a setpoint far
 * outside the observed window compresses the trace rather than vanishing off
 * the top of the box: "nowhere near the threshold" is exactly what that picture
 * should say.
 */
export function sparkGeometry(
  series: readonly SeriesPoint[],
  threshold: number | null,
  box: SparkBox
): SparkGeometry | null {
  const usable = series.filter((p) => Number.isFinite(p.t) && Number.isFinite(p.v));
  const first = usable[0];
  const last = usable[usable.length - 1];
  if (first === undefined || last === undefined) return null;

  const padY = box.padY ?? 2;
  const top = padY;
  const bottom = Math.max(padY, box.height - padY);

  let min = first.v;
  let max = first.v;
  for (const p of usable) {
    if (p.v < min) min = p.v;
    if (p.v > max) max = p.v;
  }
  if (threshold !== null && Number.isFinite(threshold)) {
    min = Math.min(min, threshold);
    max = Math.max(max, threshold);
  }

  const span = max - min;
  const yFor = (v: number): number =>
    span === 0 ? (top + bottom) / 2 : bottom - ((v - min) / span) * (bottom - top);

  const firstT = first.t;
  const tSpan = last.t - firstT;
  const xFor = (t: number, index: number): number => {
    // A single sample, or a window whose stamps all collapsed onto one instant,
    // has no time axis to spread along — fall back to even spacing so the shape
    // is still drawn rather than stacked on x=0.
    if (tSpan <= 0) {
      return usable.length === 1 ? box.width / 2 : (index / (usable.length - 1)) * box.width;
    }
    return ((t - firstT) / tSpan) * box.width;
  };

  const points = usable.map((p, i) => `${round(xFor(p.t, i))},${round(yFor(p.v))}`).join(' ');

  return {
    points,
    thresholdY: threshold !== null && Number.isFinite(threshold) ? round(yFor(threshold)) : null,
    min,
    max,
  };
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}

// ---------------------------------------------------------------------------
// Mini-bar (the ALL ALARMS "NOW" cell).
// ---------------------------------------------------------------------------

/** Where to draw the fill and the threshold tick, as percentages 0..100. */
export interface MiniBarModel {
  /** Fill width, 0..100. */
  readonly pct: number;
  /** Threshold tick position, 0..100, or `null` when there is no threshold. */
  readonly tickPct: number | null;
}

/**
 * Scale a current value (and its threshold) against an OBSERVED window.
 *
 * `null` when there is no window — the catalog carries no per-signal min/max,
 * so without a real observed range there is nothing to scale against and the
 * row renders value + threshold as text instead. Inventing a 0..100 domain for
 * volts is the exact fabrication this returns `null` to prevent.
 *
 * The domain is padded to include the threshold for the same reason the
 * sparkline's is: a tick pinned to the edge of the bar because the setpoint sat
 * outside the day's readings would misreport how close the boat is to it.
 */
export function miniBarModel(input: {
  readonly min: number | null | undefined;
  readonly max: number | null | undefined;
  readonly value: number | null | undefined;
  readonly threshold: number | null | undefined;
}): MiniBarModel | null {
  const { min, max, value, threshold } = input;
  if (min === null || min === undefined || !Number.isFinite(min)) return null;
  if (max === null || max === undefined || !Number.isFinite(max)) return null;
  if (value === null || value === undefined || !Number.isFinite(value)) return null;

  const hasThreshold = threshold !== null && threshold !== undefined && Number.isFinite(threshold);
  let lo = Math.min(min, max);
  let hi = Math.max(min, max);
  if (hasThreshold) {
    lo = Math.min(lo, threshold);
    hi = Math.max(hi, threshold);
  }
  // The live value can sit outside the window it was scaled against (the series
  // is a downsampled bucket average; the reading is instantaneous). Include it
  // rather than clamp, so the fill never reads as "at the top" when it is past
  // the top.
  lo = Math.min(lo, value);
  hi = Math.max(hi, value);

  const span = hi - lo;
  // Every number in play is the same number: nothing to place. A half-full bar
  // would be a guess, so the row falls back to text.
  if (span === 0) return null;

  const pctFor = (v: number): number => round(((v - lo) / span) * 100);
  return {
    pct: pctFor(value),
    tickPct: hasThreshold ? pctFor(threshold) : null,
  };
}
